import type { ReactNode } from 'react';

import { FlySvgSprite } from '@fly/svg';
import { DropdownMenu as RadixDropdownMenu, Separator } from '@radix-ui/themes';
import cn from 'classnames';

import { Text } from '../Text';
import { SEPARATOR_TITLE, getFlyToRadixColor, renderIcon } from '../utils';
import styles from './DropdownMenu.module.less';
import type {
  DropdownMenuItemProps,
  DropdownMenuProps,
} from './DropdownMenu.types';

const itemRowClassName =
  'flex cursor-pointer items-start justify-between gap-fly-2 py-[6px]';

function renderMenuItems(
  items: DropdownMenuItemProps[],
  keyPrefix: string
): ReactNode[] {
  return items.map((item, index) => {
    if (item.title === SEPARATOR_TITLE) {
      return <Separator key={`${keyPrefix}-${index}`} size='4' />;
    }

    const {
      title,
      subTitle,
      iconStart,
      iconEnd,
      color,
      subItems,
      onClick,
      className,
      ...rest
    } = item;

    const row = (
      <>
        <div className='flex items-center gap-fly-3'>
          {iconStart && renderIcon(iconStart, 16, 'mt-fly-1')}
          <div className='flex flex-col'>
            <Text size='2'>{title}</Text>
            {subTitle && (
              <Text size='1' highContrast={false} className={styles.subTitle}>
                {subTitle}
              </Text>
            )}
          </div>
        </div>
        {iconEnd && renderIcon(iconEnd, 16, 'mt-fly-1')}
      </>
    );

    const itemColor = color ? getFlyToRadixColor(color) : 'gray';

    if (subItems?.length) {
      const subKey = `${keyPrefix}-sub-${index}-${title}`;
      return (
        <RadixDropdownMenu.Sub key={subKey}>
          <RadixDropdownMenu.SubTrigger
            className={itemRowClassName}
            {...{ 'data-accent-color': itemColor }}
          >
            <div
              className={cn(
                'min-w-0 flex flex-1 items-start justify-between gap-fly-3',
                className
              )}
              {...rest}
            >
              {row}
            </div>
            <FlySvgSprite
              name='single_arrow_right'
              size={16}
              className='mt-fly-1'
            />
          </RadixDropdownMenu.SubTrigger>
          <RadixDropdownMenu.SubContent
            onClick={(e) => e.stopPropagation()}
            className={cn(
              styles.dropdownMenuContent,
              'relative left-[25px] min-w-[180px]'
            )}
          >
            <div className='flex flex-col gap-fly-3'>
              {renderMenuItems(subItems, subKey)}
            </div>
          </RadixDropdownMenu.SubContent>
        </RadixDropdownMenu.Sub>
      );
    }

    return (
      <RadixDropdownMenu.Item
        key={`${keyPrefix}-item-${index}-${title}`}
        onClick={onClick}
        className={itemRowClassName}
        color={itemColor}
      >
        <div className={cn('contents', className)} {...rest}>
          {row}
        </div>
      </RadixDropdownMenu.Item>
    );
  });
}

export function DropdownMenu({
  trigger,
  items,
  disabled,
  hideContentWhenClosed = false,
  ...props
}: DropdownMenuProps) {
  return (
    <RadixDropdownMenu.Root {...props}>
      <RadixDropdownMenu.Trigger disabled={disabled}>
        {trigger}
      </RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Content
        onClick={(e) => e.stopPropagation()}
        className={cn(
          styles.dropdownMenuContent,
          'min-w-[180px]',
          hideContentWhenClosed && 'data-[state=closed]:hidden'
        )}
      >
        <div className='flex flex-col gap-fly-3'>
          {renderMenuItems(items, 'menu')}
        </div>
      </RadixDropdownMenu.Content>
    </RadixDropdownMenu.Root>
  );
}
